/**
 * Client error log persistence for admin review.
 * Collection: errorLogs — create via ErrorReportProvider; list/update admin-only.
 */

import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';

export const ERROR_LOGS_COLLECTION = 'errorLogs';

export const ERROR_LOG_STATUSES = Object.freeze(['new', 'resolved']);

export const ERROR_LOG_KINDS = Object.freeze(['error', 'intent']);

const STACK_MAX = 4000;

/**
 * Build the Firestore payload for a client error report (no write).
 * @param {object} fields
 * @returns {object}
 */
export function buildErrorLogPayload(fields = {}) {
  const message = String(fields.message || 'Something went wrong.').trim() || 'Something went wrong.';
  const detail =
    fields.detail == null || fields.detail === ''
      ? null
      : String(fields.detail).slice(0, 2000);
  const stack =
    fields.stack == null || fields.stack === ''
      ? null
      : String(fields.stack).slice(0, STACK_MAX);
  const status = ERROR_LOG_STATUSES.includes(fields.status) ? fields.status : 'new';
  const kind = ERROR_LOG_KINDS.includes(fields.kind) ? fields.kind : 'error';

  return {
    message,
    detail,
    stack,
    pathname: String(fields.pathname || '/'),
    pageTitle: String(fields.pageTitle || ''),
    pageLabel: fields.pageLabel ? String(fields.pageLabel) : null,
    uid: fields.uid ? String(fields.uid) : null,
    email: fields.email ? String(fields.email) : null,
    userAgent: fields.userAgent ? String(fields.userAgent).slice(0, 500) : null,
    source: String(fields.source || 'app'),
    kind,
    status,
    createdAt: fields.createdAt ?? serverTimestamp(),
  };
}

/**
 * Persist an error log. Caller must be authenticated; uid must match auth uid (rules).
 * @param {object} fields
 * @returns {Promise<string>} new doc id
 */
export async function createErrorLog(fields) {
  const payload = buildErrorLogPayload(fields);
  if (!payload.uid) {
    throw new Error('errorLogs create requires uid');
  }
  const ref = await addDoc(collection(db, ERROR_LOGS_COLLECTION), payload);
  return ref.id;
}

/**
 * Admin list — newest first.
 * @param {{ limitCount?: number }} [opts]
 * @returns {Promise<object[]>}
 */
export async function listErrorLogs({ limitCount = 100 } = {}) {
  const q = query(
    collection(db, ERROR_LOGS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(Math.min(Math.max(Number(limitCount) || 100, 1), 500))
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Admin status update (new | resolved).
 * @param {string} id
 * @param {'new'|'resolved'} status
 */
export async function updateErrorLogStatus(id, status) {
  if (!id) throw new Error('Missing error log id');
  if (!ERROR_LOG_STATUSES.includes(status)) {
    throw new Error(`Invalid status: ${status}`);
  }
  await updateDoc(doc(db, ERROR_LOGS_COLLECTION, id), { status });
}
